import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import SEO from "../../components/common/SEO";
import {
  generateSportsEventSchema,
  generateBreadcrumbSchema,
} from "../../utils/seo/structuredData";
import { useFetchUser } from "../../hooks/user/useFetchUser";
import { useEventsWithLeagueCount } from "../../hooks/events/useEventsWithLeagueCount";
import { useFetchUserEventLineup } from "../../hooks/lineup/useFetchUserEventLineup";
import {
  CalendarIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
} from "@heroicons/react/outline";
import { ExclamationIcon, InformationCircleIcon } from "@heroicons/react/solid";
import Loading from "../../components/common/Loading";
import CountdownTimer from "../../components/common/CountdownTimer";
import LineupSubmission from "../../components/lineup/LineupSubmission";
import { useDispatch } from "react-redux";
import { setLeagueId } from "../../features/league/leagueReducer";
import { setEventId, setEventName } from "../../features/event/eventReducer";
import { useEventDetails } from "../../hooks/events/useEventDetails";
import { isEventEligibleForLeague } from "../../utils/league/leagueHelpers";
import { isEventThisWeek } from "../../utils/eventTimingHelper";
import ReadOnlyFightCard from "../../components/events/ReadOnlyFightCard";

const EventDetails = () => {
  const dispatch = useDispatch();
  const { eventId } = useParams();

  const {
    data: eventDetails,
    isLoading: eventLoading,
    error: eventError,
  } = useEventDetails(eventId);

  // Update Redux state when eventDetails loads
  useEffect(() => {
    if (eventDetails?.Name && eventId) {
      dispatch(setEventId(eventId));
      dispatch(setEventName(eventDetails.Name));
    }
  }, [eventDetails?.Name, eventId, dispatch]);

  // State for expanded leagues
  const [expandedLeagues, setExpandedLeagues] = useState(new Set());

  // State for lineup modal
  const [showLineupModal, setShowLineupModal] = useState(false);
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);

  const {
    data: user,
    isLoading: userLoading,
    error: userError,
  } = useFetchUser();

  // Get events with league info
  const { events } = useEventsWithLeagueCount();

  const currentEvent = events?.find(
    (event) => event.EventId === eventId || event.EventId === parseInt(eventId),
  );

  const userLeagues = currentEvent?.userLeagues || [];

  // Check if event is happening this week (lineup submission allowed)
  const isThisWeek = eventDetails?.DateTime
    ? isEventThisWeek(eventDetails.DateTime)
    : false;

  // Admin can explicitly release an event for lineup creation by setting status to "ready"
  const isEventReady = eventDetails?.Status === "ready";

  // Check if event has started or is in progress
  const eventHasStarted = eventDetails?.DateTime
    ? new Date(eventDetails.DateTime) <= new Date()
    : false;

  // Determine if we should show read-only mode
  // Read-only if: event hasn't started this week AND admin hasn't marked it ready,
  // OR event has already started
  const isReadOnly = (!isThisWeek && !isEventReady) || eventHasStarted;

  // Add global league if not already included
  const globalLeagueId = process.env.REACT_APP_GLOBAL_LEAGUE_ID;
  const allLeaguesUnfiltered = [
    { id: globalLeagueId, name: "Global Competition", eventIds: [] }, // Global league has no restrictions
    ...userLeagues,
  ];

  // Filter leagues to only show those where this event is eligible
  const allLeagues = allLeaguesUnfiltered.filter((league) => {
    // Global league is always eligible
    if (league.id === globalLeagueId) {
      return true;
    }

    // Check if event is eligible for this league
    return isEventEligibleForLeague(eventId, league, eventDetails?.DateTime);
  });

  // Track ineligible leagues for display purposes
  const ineligibleLeagues = allLeaguesUnfiltered.filter((league) => {
    if (league.id === globalLeagueId) {
      return false;
    }
    return !isEventEligibleForLeague(eventId, league, eventDetails?.DateTime);
  });

  // Fetch lineup submissions for global league
  const globalSubmission = useFetchUserEventLineup(
    globalLeagueId,
    eventId,
    user?._id,
    true,
  );

  // Fetch lineup submissions for user leagues (up to a reasonable limit)
  const league1Submission = useFetchUserEventLineup(
    userLeagues[0]?.id,
    eventId,
    user?._id,
    Boolean(userLeagues[0]?.id),
  );

  const league2Submission = useFetchUserEventLineup(
    userLeagues[1]?.id,
    eventId,
    user?._id,
    Boolean(userLeagues[1]?.id),
  );

  const league3Submission = useFetchUserEventLineup(
    userLeagues[2]?.id,
    eventId,
    user?._id,
    Boolean(userLeagues[2]?.id),
  );

  const league4Submission = useFetchUserEventLineup(
    userLeagues[3]?.id,
    eventId,
    user?._id,
    Boolean(userLeagues[3]?.id),
  );

  const league5Submission = useFetchUserEventLineup(
    userLeagues[4]?.id,
    eventId,
    user?._id,
    Boolean(userLeagues[4]?.id),
  );

  // Helper function to get submission status for a league
  const getLeagueSubmissionStatus = (leagueId) => {
    let submissionQuery;

    if (leagueId === globalLeagueId) {
      submissionQuery = globalSubmission;
    } else if (leagueId === userLeagues[0]?.id) {
      submissionQuery = league1Submission;
    } else if (leagueId === userLeagues[1]?.id) {
      submissionQuery = league2Submission;
    } else if (leagueId === userLeagues[2]?.id) {
      submissionQuery = league3Submission;
    } else if (leagueId === userLeagues[3]?.id) {
      submissionQuery = league4Submission;
    } else if (leagueId === userLeagues[4]?.id) {
      submissionQuery = league5Submission;
    }

    if (submissionQuery) {
      const submission = submissionQuery.data;
      return {
        hasSubmission: Boolean(
          submission &&
          submission.selections &&
          submission.selections.length > 0,
        ),
        submission,
      };
    }

    return { hasSubmission: false, submission: null };
  };

  const toggleLeagueExpansion = (leagueId) => {
    const newExpanded = new Set(expandedLeagues);
    if (newExpanded.has(leagueId)) {
      newExpanded.delete(leagueId);
    } else {
      newExpanded.add(leagueId);
    }
    setExpandedLeagues(newExpanded);
  };

  const handleCreateLineup = (leagueId) => {
    console.log("🎬 handleCreateLineup called with leagueId:", leagueId);
    dispatch(setLeagueId(leagueId));
    setSelectedLeagueId(leagueId);
    setShowLineupModal(true);
  };

  const handleViewLineup = (leagueId) => {
    console.log("👁️ handleViewLineup called with leagueId:", leagueId);
    dispatch(setLeagueId(leagueId));
    setSelectedLeagueId(leagueId);
    setShowLineupModal(true);
  };

  const handleCloseLineupModal = () => {
    setShowLineupModal(false);
    // Don't reset selectedLeagueId - keep it so draft persists
    // setSelectedLeagueId(null);
  };

  const getModalTitle = () => {
    if (!selectedLeagueId) return "Lineup";

    const league = allLeagues.find((l) => l.id === selectedLeagueId);
    const leagueName = league?.name || "League";
    const hasSubmission =
      getLeagueSubmissionStatus(selectedLeagueId).hasSubmission;

    if (hasSubmission) {
      return `${leagueName} Lineup`;
    } else {
      return `Create ${leagueName} Lineup`;
    }
  };

  if (eventLoading || userLoading) return <Loading />;
  if (eventError)
    return <div>Error loading event details: {eventError.message}</div>;
  if (userError) return <div>Error loading user data: {userError.message}</div>;

  const breadcrumbs = [
    { name: "Home", path: "/home" },
    { name: "Events", path: "/events" },
    { name: eventDetails?.Name || "Event Details", path: `/events/${eventId}` },
  ];

  const eventSchema = eventDetails
    ? generateSportsEventSchema({
        name: eventDetails.Name,
        date: eventDetails.DateTime,
        location: eventDetails.Location,
      })
    : null;

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [eventSchema, generateBreadcrumbSchema(breadcrumbs)].filter(
      Boolean,
    ),
  };

  return (
    <div className="space-y-4">
      <SEO
        title={`${eventDetails?.Name || "Event"} - UFC Fantasy Lineup | DayShift MMA`}
        description={`Build your fantasy lineup for ${eventDetails?.Name || "this UFC event"}. Select fighters, compete in leagues, and track your performance in real-time.`}
        keywords="UFC fantasy lineup, MMA fantasy picks, UFC event lineup, fantasy fighter selection, UFC fantasy competition"
        url={`/events/${eventId}`}
        structuredData={structuredData}
      />

      {/* Event Header - Mobile First */}
      <div className="relative bg-gradient-to-br from-deep-red via-deep-red to-bright-red rounded-xl shadow-lg overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-24 h-24 bg-white rounded-full -translate-y-12 translate-x-12"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-white rounded-full translate-y-8 -translate-x-8"></div>
        </div>

        <div className="relative p-4">
          <h1 className="text-xl font-bold text-white mb-3 leading-tight">
            {eventDetails.Name}
          </h1>

          {/* Countdown Timer */}
          {new Date(eventDetails.DateTime) > new Date() && (
            <div className="mb-3 flex justify-center">
              <CountdownTimer
                targetDate={eventDetails.DateTime}
                className="bg-white/20 backdrop-blur-sm text-white border border-white/30"
                showIcon={true}
              />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4 text-white/90 text-sm mb-4">
            <div className="flex items-center gap-1.5">
              <CalendarIcon className="w-4 h-4" />
              <span>
                {new Date(eventDetails.DateTime).toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <ClockIcon className="w-4 h-4" />
              <span>
                {new Date(eventDetails.DateTime).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>

          {/* Event Stats - Mobile First */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20">
              <div className="text-2xl font-bold text-white">
                {eventDetails.Fights?.filter((f) => !f.hidden).length || 0}
              </div>
              <div className="text-white/80 text-xs">Total Fights</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20">
              <div className="text-2xl font-bold text-white">
                {allLeagues.length}
              </div>
              <div className="text-white/80 text-xs">Your Leagues</div>
            </div>
          </div>
        </div>
      </div>

      {/* Conditional Content Based on Event Timing */}
      {isReadOnly ? (
        // READ-ONLY MODE: Event hasn't started this week OR has already started
        <div className="space-y-4">
          {/* Info Banner */}
          <div
            className={`${eventHasStarted ? "bg-gray-50 border-gray-200" : "bg-blue-50 border-blue-200"} border rounded-xl p-4`}
          >
            <div className="flex items-start gap-3">
              <InformationCircleIcon
                className={`${eventHasStarted ? "text-medium-grey" : "text-blue-600"} w-5 h-5 mt-0.5 flex-shrink-0`}
              />
              <div className="flex-1">
                {eventHasStarted ? (
                  <p className="text-sm text-medium-grey">
                    <span className="font-semibold">
                      This event has started or is in progress.
                    </span>{" "}
                    Lineup submissions are now closed. You can view the fight
                    card and results below.
                  </p>
                ) : (
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">
                      Lineup submission opens this week.
                    </span>{" "}
                    You can view the fight card below, but lineup creation will
                    be available once the event is within the current week
                    (Sunday-Saturday).
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Fight Card Header */}
          <div className="flex items-center justify-between px-1">
            <h2 className="text-lg font-bold text-dark-grey">Fight Card</h2>
            <div className="text-xs text-medium-grey bg-gray-100 px-2.5 py-1 rounded-full font-medium">
              {eventDetails.Fights?.filter((f) => !f.hidden).length || 0} fights
            </div>
          </div>

          {/* Read-Only Fight Card */}
          <ReadOnlyFightCard fights={eventDetails.Fights} />
        </div>
      ) : (
        // LINEUP SUBMISSION MODE: Event is this week and hasn't started yet
        <>
          {/* Leagues Section - Mobile First */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-lg font-bold text-dark-grey">Leagues</h2>
              <div className="text-xs text-medium-grey bg-gray-100 px-2.5 py-1 rounded-full font-medium">
                {allLeagues.length}
              </div>
            </div>

            {/* Info banner when leagues are filtered out */}
            {ineligibleLeagues.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <ExclamationIcon className="text-yellow-600 w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-yellow-800">
                      <span className="font-semibold">
                        {ineligibleLeagues.length} league
                        {ineligibleLeagues.length > 1 ? "s are" : " is"} not
                        shown
                      </span>{" "}
                      because this event is not part of{" "}
                      {ineligibleLeagues.length > 1 ? "their" : "its"} schedule.
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Leagues only include specific events selected during
                      creation.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {allLeagues.length === 0 && (
              <div className="bg-gray-50 rounded-xl p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ExclamationIcon className="text-gray-400 w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-dark-grey mb-2">
                  No Eligible Leagues
                </h3>
                <p className="text-medium-grey mb-4">
                  This event is not part of any of your leagues. You can still
                  compete in the Global Competition.
                </p>
              </div>
            )}

            {allLeagues.map((league) => {
              const { hasSubmission, submission } = getLeagueSubmissionStatus(
                league.id,
              );

              return (
                <div
                  key={league.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-200"
                >
                  {/* League Header with Smart Status */}
                  <div className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-base text-dark-grey">
                            {league.name}
                          </h3>
                          {league.id === globalLeagueId && (
                            <span className="text-xs bg-gold text-dark-grey px-2 py-1 rounded-full font-bold">
                              Global
                            </span>
                          )}
                          {hasSubmission && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                              Submitted
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Smart Action Button */}
                      <div className="flex gap-2">
                        {hasSubmission ? (
                          <>
                            <button
                              onClick={() => handleViewLineup(league.id)}
                              className="flex-1 flex items-center justify-center gap-2 bg-deep-red text-white py-3.5 px-4 rounded-xl text-sm font-bold hover:bg-bright-red transition-colors"
                            >
                              <EyeIcon className="w-4 h-4" />
                              View Lineup
                            </button>
                            <button
                              onClick={() => toggleLeagueExpansion(league.id)}
                              className="px-4 py-3.5 bg-gray-100 text-dark-grey rounded-xl hover:bg-gray-200 transition-colors"
                            >
                              {expandedLeagues.has(league.id) ? (
                                <ChevronUpIcon className="w-4 h-4" />
                              ) : (
                                <ChevronDownIcon className="w-4 h-4" />
                              )}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleCreateLineup(league.id)}
                            className="w-full flex items-center justify-center gap-2 bg-deep-red text-white py-3.5 px-4 rounded-xl text-sm font-bold hover:bg-bright-red transition-colors"
                          >
                            <PlusIcon className="w-4 h-4" />
                            Create Lineup
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content - Enhanced Submissions View */}
                  {expandedLeagues.has(league.id) &&
                    hasSubmission &&
                    submission && (
                      <div className="border-t border-gray-100 bg-gradient-to-b from-gray-50/50 to-gray-50/80">
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-bold text-dark-grey">
                              Your Lineup
                            </h4>
                          </div>

                          {/* Lineup Summary */}
                          <div className="space-y-2">
                            <div className="bg-white rounded-lg p-3 border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-bold text-dark-grey">
                                    Primary Lineup
                                  </span>
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                                    Complete
                                  </span>
                                </div>
                                <button
                                  onClick={() => handleViewLineup(league.id)}
                                  className="text-sm text-deep-red font-bold hover:text-bright-red transition-colors px-3 py-1.5 rounded-lg hover:bg-red-50 flex items-center gap-1"
                                >
                                  <PencilIcon className="w-3 h-3" />
                                  Edit
                                </button>
                              </div>
                              <div className="text-xs text-medium-grey">
                                5 fighters selected • LOTN:{" "}
                                {submission.selections?.find((s) => s.lotn)
                                  ?.fighter || "None"}
                              </div>
                              {submission.alternates &&
                                submission.alternates.length > 0 && (
                                  <div className="text-xs text-medium-grey mt-1">
                                    Alternates: {submission.alternates.length}/2
                                    selected
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Empty State for No Submissions */}
                  {expandedLeagues.has(league.id) && !hasSubmission && (
                    <div className="border-t border-gray-100 bg-gray-50/50">
                      <div className="p-4 text-center">
                        <div className="text-sm text-medium-grey mb-1">
                          No lineup submitted
                        </div>
                        <div className="text-xs text-medium-grey">
                          Create your first lineup above ↑
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Admin Controls - Mobile First */}
      {/* {isAdmin && (
        <div className="bg-gradient-to-r from-gray-900 to-dark-grey rounded-xl shadow-lg overflow-hidden">
          <div className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 bg-gold rounded-full flex items-center justify-center">
                <span className="text-sm">⚙️</span>
              </div>
              <h3 className="font-bold text-base text-white">Admin Controls</h3>
            </div>

            <div className="space-y-3">
              <button
                onClick={() =>
                  handleStatusChange({ target: { value: "in progress" } })
                }
                className={`w-full px-4 py-3 rounded-xl text-sm font-bold transition-colors ${
                  status === "in progress"
                    ? "bg-deep-red text-white"
                    : "bg-white/10 text-white hover:bg-white/20 border border-white/20"
                }`}
              >
                In Progress
              </button>

              <button
                onClick={handleUpdateLeagueMemberScores}
                className="w-full bg-deep-red text-white py-3 rounded-xl text-sm font-bold hover:bg-bright-red transition-colors"
              >
                Update League Member Scores
              </button>

              <button
                onClick={handleAutoSelectLineup}
                disabled={isAutoSelectLoading}
                className={`w-full py-3 rounded-xl text-sm font-bold transition-colors ${
                  isAutoSelectLoading
                    ? "bg-gray-600 text-white cursor-not-allowed"
                    : "bg-gold text-dark-grey hover:bg-yellow-500"
                }`}
              >
                {isAutoSelectLoading ? "Creating..." : "Auto Select Lineups"}
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* Lineup Modal */}
      {showLineupModal && (
        <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          {/* Modal container - looks like a modal on all screen sizes */}
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-deep-red to-bright-red text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  {getLeagueSubmissionStatus(selectedLeagueId).hasSubmission ? (
                    <EyeIcon className="w-4 h-4" />
                  ) : (
                    <PlusIcon className="w-4 h-4" />
                  )}
                </div>
                <h2 className="text-lg font-bold">{getModalTitle()}</h2>
              </div>
              <button
                onClick={handleCloseLineupModal}
                className="text-white hover:text-gray-200 w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <LineupSubmission
                key={`${selectedLeagueId}-${eventId}`} // Force remount when league OR event changes
                eventId={eventId}
                leagueId={selectedLeagueId}
                userId={user?._id} // Pass userId from API, not Redux
                fighters={eventDetails}
                eventStart={eventDetails.DateTime}
                onSubmissionSuccess={handleCloseLineupModal}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetails;
